import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { Mountain } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE,
} from './chartTheme';

function MiniLine({ data, yKey, xLabel, yLabel, color, idSuffix }) {
  const zoom = useChartZoom();
  return (
    <div>
      <ChartZoomHeader caption={`${yLabel} vs ${xLabel}`} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId={`zoom-reset-${idSuffix || yKey}`} />
      <ResponsiveContainer width="100%" height={200}>
        <LineChart
          data={data} margin={{ top: 4, right: 12, left: 4, bottom: 20 }}
          onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="speed_kph" type="number" domain={zoom.xDomain} allowDataOverflow
            tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: xLabel, position: 'insideBottom', offset: -8, style: AXIS_LABEL_STYLE }}
          />
          <YAxis tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} formatter={(v) => Number(v).toFixed(1)} />
          <Line type="monotone" dataKey={yKey} stroke={color} {...LINE_PROPS} />
          {zoom.isSelecting && (
            <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Gradient Climb Graphs (§6 plots 4-7): traction power and wheel torque required while
 * climbing at Max. Hill Climb Slope, shown across two speed ranges (0-10 and 0-20 kph) —
 * both use the same gradient value, differing only in x-axis speed range.
 */
const GradientClimbChart = ({ sweep010, sweep020 }) => {
  if (!sweep010 || !sweep020 || sweep010.length === 0 || sweep020.length === 0) return null;

  return (
    <ChartCard
      icon={Mountain}
      title="Gradient Climb Graphs"
      description="Wheel traction power and torque required while climbing at Max. Hill Climb Slope, across two low-speed ranges."
      testId="chart-gradient-climb"
    >
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/70 mb-2 text-center">0-10 km/h</p>
          <div className="grid grid-cols-2 gap-4">
            <MiniLine data={sweep010} yKey="wheel_power_kw" xLabel="Speed (km/h)" yLabel="Traction Power (kW)" color={SERIES_COLORS[3]} idSuffix="power-010" />
            <MiniLine data={sweep010} yKey="wheel_torque_nm" xLabel="Speed (km/h)" yLabel="Wheel Torque (Nm)" color={SERIES_COLORS[4]} idSuffix="torque-010" />
          </div>
        </div>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/70 mb-2 text-center">0-20 km/h</p>
          <div className="grid grid-cols-2 gap-4">
            <MiniLine data={sweep020} yKey="wheel_power_kw" xLabel="Speed (km/h)" yLabel="Traction Power (kW)" color={SERIES_COLORS[5]} idSuffix="power-020" />
            <MiniLine data={sweep020} yKey="wheel_torque_nm" xLabel="Speed (km/h)" yLabel="Wheel Torque (Nm)" color={SERIES_COLORS[6]} idSuffix="torque-020" />
          </div>
        </div>
      </div>
    </ChartCard>
  );
};

export default GradientClimbChart;
